/**
 * My Event Card Component
 * Displays a single event in the My Events tab
 */
import React from 'react';
import { Calendar, MapPin, ExternalLink, X } from 'lucide-react';

const EDMTRAIN_LOGO = "https://d2po6uops3e7id.cloudfront.net/img/logo/icon-square-transparent-bg-web.svg";

const MyEventCard = ({
  event,
  formattedDate,
  onViewInCalendar,
  onRemove,
  onDiscordShare
}) => {
  return (
    <div
      className="event-card relative rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.01] hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-500/20 group"
      style={{ transform: 'translateZ(0)', boxSizing: 'border-box', maxWidth: '100%' }}
    >
      {/* Gradient border wrapper - same as Upcoming Events */}
      <div 
        className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500 via-pink-500 to-cyan-500 p-[2px] opacity-70 group-hover:opacity-100 transition-opacity"
        style={{ 
          backgroundSize: '200% 100%',
          animation: 'gradientSlide 3s ease infinite'
        }}
      />
      
      <div className="relative bg-gray-900 rounded-xl m-[2px] flex overflow-hidden min-h-[140px]" style={{ boxSizing: 'border-box' }}>
        {/* Event Image - matches height of card */}
        <div className="w-36 min-h-[140px] flex-shrink-0 self-stretch relative bg-gradient-to-br from-cyan-500/20 to-pink-500/20">
          {event.event_image_url ? (
            <img 
              src={event.event_image_url} 
              alt={event.event_name}
              className="w-full h-full object-cover absolute inset-0"
            />
          ) : event.event_source === 'edmtrain' ? (
            /* EDMtrain logo placeholder - fills the space */
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-gray-900 to-cyan-900 flex flex-col items-center justify-center p-3">
              <img 
                src={EDMTRAIN_LOGO} 
                alt="EDMtrain"
                className="w-20 h-20 object-contain flex-shrink-0"
              />
              <span className="text-white/70 text-xs font-medium mt-2">via EDMtrain</span>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Calendar className="text-cyan-400" size={40} />
            </div>
          )}
        </div>
        
        {/* Event Info */}
        <div className="flex-1 p-4 min-w-0 overflow-hidden" style={{ minWidth: 0 }}>
          <div className="flex items-start justify-between mb-2">
            <h4 className="text-lg font-bold text-white line-clamp-1">{event.event_name}</h4>
            <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-xs font-medium flex-shrink-0 ml-2">
              🎫 Going
            </span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar size={14} className="text-cyan-400" />
              <span>{formattedDate}</span>
            </div>
            
            {event.event_venue && (
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin size={14} className="text-pink-400" />
                <span className="line-clamp-1">{event.event_venue}</span>
              </div>
            )}
            
            {event.event_location && (
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin size={14} className="text-gray-500" />
                <span className="line-clamp-1">{event.event_location}</span>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-3 mt-4">
            {event.event_url && (
              <a
                href={event.event_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm bg-gradient-to-r from-cyan-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:from-cyan-400 hover:to-pink-400 transition-all"
              >
                <ExternalLink size={14} className="inline mr-1" />
                View Event
              </a>
            )}
            
            {/* Discord Share - Just logo icon */}
            {onDiscordShare && (
              <button
                onClick={() => onDiscordShare({
                  title: event.event_name,
                  artist: event.event_venue,
                  artwork: event.event_image_url || '',
                  genre: event.event_genre || 'EDM',
                  location: event.event_location,
                  date: event.event_date,
                  url: event.event_url || ''
                })}
                className="p-1.5 bg-[#5865F2]/15 border border-[#5865F2]/30 rounded-lg hover:bg-[#5865F2]/25 transition-all"
                title="Share to Discord"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#7289da">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </button>
            )}
            
            <button
              onClick={onViewInCalendar}
              className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1 transition-colors"
            >
              <Calendar size={14} />
              View in Calendar
            </button>
            
            {/* Remove from My Events */}
            <button
              onClick={onRemove}
              className="px-2 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 hover:text-red-300 transition-all duration-200 flex items-center gap-1 ml-auto"
              title="Remove from My Events"
            >
              <X size={12} />
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyEventCard;
